// ============================================
// FAMUZA MARKET — Admin Panel
// UNIFAM GROUP LTD — Platform Control
// ============================================

import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useApp } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';

const ADMIN_IDS = ['f8de3126-ff1a-490f-91e9-3d308883bf54'];
const formatPrice = (p) => `K${Number(p).toLocaleString()}`;

function isAdmin(user, profile) {
  if (!user) return false;
  return profile?.role === 'admin' || ADMIN_IDS.includes(user.id);
}

export default function AdminPanel() {
  const { user, loading: authLoading } = useApp();
  const profile = null; // profile/role handled via ADMIN_IDS hardcoded check
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/seller/auth'); return; }
    if (!isAdmin(user, profile)) { navigate('/'); return; }
    fetchAll();
  }, [user, profile, authLoading]);

  async function fetchAll() {
    setLoading(true);
    const { data: productsData } = await supabase
      .from('products').select('*').order('created_at', { ascending: false });
    const { data: sellersData } = await supabase
      .from('sellers').select('*');
    const { data: profilesData } = await supabase
      .from('profiles').select('*');

    const p = productsData || [];
    const s = sellersData || [];

    setProducts(p);
    setSellers(s);

    const today = new Date().toDateString();
    setStats({
      totalProducts: p.length,
      activeProducts: p.filter(x => x.status === 'active').length,
      totalSellers: s.length,
      verifiedSellers: s.filter(x => x.verified).length,
      totalViews: p.reduce((sum, x) => sum + (x.views || 0), 0),
      totalProfiles: (profilesData || []).length,
      featuredProducts: p.filter(x => x.is_featured).length,
      dealsCount: p.filter(x => x.old_price).length,
      addedToday: p.filter(x => new Date(x.created_at).toDateString() === today).length,
      newSellersToday: s.filter(x => new Date(x.created_at).toDateString() === today).length,
      lowStock: p.filter(x => x.stock === 1).length,
    });
    setLoading(false);
  }

  async function toggleFeature(product) {
    await supabase.from('products').update({ is_featured: !product.is_featured }).eq('id', product.id);
    fetchAll();
  }

  async function toggleProductStatus(product) {
    const newStatus = product.status === 'active' ? 'suspended' : 'active';
    await supabase.from('products').update({ status: newStatus }).eq('id', product.id);
    fetchAll();
  }

  async function toggleSellerVerified(seller) {
    await supabase.from('sellers').update({ verified: !seller.verified }).eq('id', seller.id);
    fetchAll();
  }

  async function toggleSellerStatus(seller) {
    const newStatus = seller.status === 'suspended' ? 'active' : 'suspended';
    await supabase.from('sellers').update({ status: newStatus }).eq('id', seller.id);
    fetchAll();
  }

  async function deleteProduct(id) {
    if (!window.confirm('Delete this product permanently?')) return;
    await supabase.from('products').delete().eq('id', id);
    fetchAll();
  }

  if (authLoading) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(245,240,232,0.4)', fontFamily: 'DM Sans, sans-serif' }}>
      Loading...
    </div>
  );

  if (!user || !isAdmin(user, profile)) return null;

  const tabs = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'products', label: `📦 Products (${stats.totalProducts || 0})` },
    { id: 'sellers', label: `👥 Sellers (${stats.totalSellers || 0})` },
  ];

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>

      {/* Nav */}
      <div style={{ background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: '#F5F0E8' }}>
              FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
            </div>
          </Link>
          <div style={{ background: 'rgba(232,98,42,0.15)', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 100, padding: '2px 10px', fontSize: '0.7rem', color: '#E8622A', fontWeight: 700 }}>
            ADMIN
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <Link to="/listings" style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', textDecoration: 'none' }}>View Market</Link>
          <Link to="/seller/dashboard" style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', textDecoration: 'none' }}>My Listings</Link>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem 1rem' }}>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.6rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.25rem' }}>
            Admin <span style={{ color: '#E8622A' }}>Panel</span>
          </h1>
          <div style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.4)' }}>UNIFAM GROUP LTD — Platform Control</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.07)' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '0.65rem 1.1rem', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab.id ? '#E8622A' : 'transparent'}`, color: activeTab === tab.id ? '#E8622A' : 'rgba(245,240,232,0.5)', fontSize: '0.85rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', marginBottom: -1, whiteSpace: 'nowrap' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(245,240,232,0.4)' }}>Loading platform data...</div>
        )}

        {/* ── DASHBOARD TAB ── */}
        {!loading && activeTab === 'dashboard' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: '0.75rem', marginBottom: '2rem' }}>
              {[
                ['📦', 'Total Products', stats.totalProducts, '#E8622A'],
                ['✅', 'Active Listings', stats.activeProducts, '#25D366'],
                ['🆕', 'Added Today', stats.addedToday, '#4A90D9'],
                ['👥', 'Total Sellers', stats.totalSellers, '#4A90D9'],
                ['✓', 'Verified Sellers', stats.verifiedSellers, '#25D366'],
                ['🌟', 'New Sellers Today', stats.newSellersToday, '#C49A2A'],
                ['👀', 'Total Views', stats.totalViews, '#C49A2A'],
                ['⭐', 'Featured Items', stats.featuredProducts, '#C49A2A'],
                ['🔥', 'Deals Listed', stats.dealsCount, '#E8622A'],
                ['⚠️', 'Only 1 Left', stats.lowStock, '#E8622A'],
                ['👤', 'Registered Users', stats.totalProfiles, '#4A90D9'],
              ].map(([icon, label, value, color]) => (
                <div key={label} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{icon}</div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.6rem', fontWeight: 800, color }}>{value ?? 0}</div>
                  <div style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.4)' }}>{label}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '1rem' }}>🆕 Recently Listed</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {products.slice(0, 6).map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
                    <img src={p.image_url} alt={p.title} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', background: '#0D0D0D', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', color: '#F5F0E8', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.4)' }}>{p.category} · {new Date(p.created_at).toLocaleDateString()}</div>
                    </div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#8B6914', flexShrink: 0 }}>{formatPrice(p.price)}</div>
                    <div style={{ fontSize: '0.62rem', background: p.status === 'active' ? 'rgba(37,211,102,0.1)' : 'rgba(232,98,42,0.1)', color: p.status === 'active' ? '#25D366' : '#E8622A', border: `0.5px solid ${p.status === 'active' ? 'rgba(37,211,102,0.3)' : 'rgba(232,98,42,0.3)'}`, borderRadius: 100, padding: '2px 8px', flexShrink: 0 }}>{p.status}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.25rem' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '1rem' }}>👥 Recent Sellers</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sellers.slice(0, 6).map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(232,98,42,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.9rem', color: '#E8622A', flexShrink: 0 }}>
                      {s.name ? s.name[0].toUpperCase() : 'F'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.82rem', color: '#F5F0E8', fontWeight: 500 }}>{s.name}</div>
                      <div style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.4)' }}>{s.phone} · Joined {new Date(s.created_at).toLocaleDateString()}</div>
                    </div>
                    <div style={{ fontSize: '0.62rem', background: s.verified ? 'rgba(37,211,102,0.1)' : 'rgba(255,255,255,0.05)', color: s.verified ? '#25D366' : 'rgba(245,240,232,0.3)', border: `0.5px solid ${s.verified ? 'rgba(37,211,102,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 100, padding: '2px 8px', flexShrink: 0 }}>
                      {s.verified ? '✓ Verified' : 'Pending'}
                    </div>
                    <div style={{ fontSize: '0.62rem', color: 'rgba(245,240,232,0.3)', flexShrink: 0 }}>
                      📦 {products.filter(p => p.seller_id === s.id).length}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── PRODUCTS TAB ── */}
        {!loading && activeTab === 'products' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {products.map(p => (
              <div key={p.id} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '0.875rem', display: 'flex', gap: '0.875rem', alignItems: 'center' }}>
                <img src={p.image_url} alt={p.title} style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover', background: '#0D0D0D', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#F5F0E8', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(245,240,232,0.4)', marginBottom: 4 }}>{p.category} · {p.condition} · {new Date(p.created_at).toLocaleDateString()}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#8B6914' }}>{formatPrice(p.price)}</span>
                    <span style={{ fontSize: '0.6rem', background: p.status === 'active' ? 'rgba(37,211,102,0.1)' : 'rgba(232,98,42,0.1)', color: p.status === 'active' ? '#25D366' : '#E8622A', border: `0.5px solid ${p.status === 'active' ? 'rgba(37,211,102,0.3)' : 'rgba(232,98,42,0.3)'}`, borderRadius: 100, padding: '1px 7px' }}>{p.status}</span>
                    {p.is_featured && <span style={{ fontSize: '0.6rem', background: 'rgba(196,154,42,0.1)', color: '#C49A2A', border: '0.5px solid rgba(196,154,42,0.3)', borderRadius: 100, padding: '1px 7px' }}>⭐ Featured</span>}
                    {p.old_price && <span style={{ fontSize: '0.6rem', background: 'rgba(232,98,42,0.1)', color: '#E8622A', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 100, padding: '1px 7px' }}>🔥 Deal</span>}
                    {p.stock === 1 && <span style={{ fontSize: '0.6rem', background: 'rgba(232,98,42,0.1)', color: '#E8622A', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 100, padding: '1px 7px' }}>⚠️ Only 1 left</span>}
                    <span style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.3)' }}>👀 {p.views || 0}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <button onClick={() => toggleFeature(p)}
                    style={{ padding: '0.4rem 0.75rem', background: p.is_featured ? 'rgba(196,154,42,0.15)' : 'rgba(255,255,255,0.05)', border: `0.5px solid ${p.is_featured ? 'rgba(196,154,42,0.4)' : 'rgba(255,255,255,0.1)'}`, color: p.is_featured ? '#C49A2A' : 'rgba(245,240,232,0.5)', borderRadius: 8, fontSize: '0.72rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {p.is_featured ? '⭐ Unfeature' : '☆ Feature'}
                  </button>
                  <button onClick={() => toggleProductStatus(p)}
                    style={{ padding: '0.4rem 0.75rem', background: p.status === 'active' ? 'rgba(232,98,42,0.1)' : 'rgba(37,211,102,0.1)', border: `0.5px solid ${p.status === 'active' ? 'rgba(232,98,42,0.3)' : 'rgba(37,211,102,0.3)'}`, color: p.status === 'active' ? '#E8622A' : '#25D366', borderRadius: 8, fontSize: '0.72rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                    {p.status === 'active' ? 'Suspend' : 'Activate'}
                  </button>
                  <button onClick={() => deleteProduct(p.id)}
                    style={{ padding: '0.4rem 0.75rem', background: 'rgba(232,98,42,0.05)', border: '0.5px solid rgba(232,98,42,0.2)', color: 'rgba(232,98,42,0.6)', borderRadius: 8, fontSize: '0.72rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SELLERS TAB ── */}
        {!loading && activeTab === 'sellers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {sellers.map(s => (
              <div key={s.id} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(232,98,42,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#E8622A', flexShrink: 0 }}>
                  {s.name ? s.name[0].toUpperCase() : 'F'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#F5F0E8', marginBottom: 2 }}>{s.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)', marginBottom: 4 }}>
                    📱 {s.phone} · 🏪 {s.shop_name || 'No shop name'} · Joined {new Date(s.created_at).toLocaleDateString()}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.62rem', background: s.verified ? 'rgba(37,211,102,0.1)' : 'rgba(255,255,255,0.05)', color: s.verified ? '#25D366' : 'rgba(245,240,232,0.3)', border: `0.5px solid ${s.verified ? 'rgba(37,211,102,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 100, padding: '2px 8px' }}>
                      {s.verified ? '✓ Verified' : 'Not verified'}
                    </span>
                    <span style={{ fontSize: '0.62rem', background: s.status === 'suspended' ? 'rgba(232,98,42,0.1)' : 'rgba(37,211,102,0.1)', color: s.status === 'suspended' ? '#E8622A' : '#25D366', border: `0.5px solid ${s.status === 'suspended' ? 'rgba(232,98,42,0.3)' : 'rgba(37,211,102,0.3)'}`, borderRadius: 100, padding: '2px 8px' }}>
                      {s.status || 'pending'}
                    </span>
                    <span style={{ fontSize: '0.62rem', color: 'rgba(245,240,232,0.3)', background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 100, padding: '2px 8px' }}>
                      {s.tier || 'basic'}
                    </span>
                    <span style={{ fontSize: '0.62rem', color: 'rgba(245,240,232,0.3)' }}>
                      📦 {products.filter(p => p.seller_id === s.id).length} listings
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <button onClick={() => toggleSellerVerified(s)}
                    style={{ padding: '0.4rem 0.75rem', background: s.verified ? 'rgba(232,98,42,0.1)' : 'rgba(37,211,102,0.1)', border: `0.5px solid ${s.verified ? 'rgba(232,98,42,0.3)' : 'rgba(37,211,102,0.3)'}`, color: s.verified ? '#E8622A' : '#25D366', borderRadius: 8, fontSize: '0.72rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {s.verified ? 'Unverify' : '✓ Verify'}
                  </button>
                  <button onClick={() => toggleSellerStatus(s)}
                    style={{ padding: '0.4rem 0.75rem', background: s.status === 'suspended' ? 'rgba(37,211,102,0.1)' : 'rgba(232,98,42,0.1)', border: `0.5px solid ${s.status === 'suspended' ? 'rgba(37,211,102,0.3)' : 'rgba(232,98,42,0.3)'}`, color: s.status === 'suspended' ? '#25D366' : '#E8622A', borderRadius: 8, fontSize: '0.72rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {s.status === 'suspended' ? 'Activate' : 'Suspend'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
